/**
 * MsrBookOfBusiness — Conversion Engine reporting Report C (Stage 4).
 *
 * Conversions credited to each MSR / sales position. Attribution: the booth rep
 * who reported the friend_joined catalyst (sales position via their profile),
 * with a fallback to the event's sales hierarchy. A NULL position is shown as an
 * "Unattributed" bucket. Date-filtered only.
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getMsrBookOfBusiness,
  type MsrBookRow,
} from "../../services/conversionReports.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";

const PAGE_SIZE = 50;
const n = (v: unknown): number => Number(v ?? 0);
const money = (v: unknown): string => `$${n(v).toFixed(2)}`;

const filterToRpc = (f: ReportFilterState, page: number, limit: number) => ({
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  page,
  limit,
});

const EXPORT_COLUMNS = [
  "Sales Position",
  "Conversions Linked",
  "Conversions Activated",
  "New Members",
  "New-Member Value",
];

const MsrBookOfBusiness: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [filters.dateFrom, filters.dateTo]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-msr-book", filters, page],
    queryFn: () => getMsrBookOfBusiness(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const mapRows = (pageRows: MsrBookRow[]): Record<string, unknown>[] =>
    pageRows.map((r) => ({
      "Sales Position": r.position_name || "Unattributed",
      "Conversions Linked": n(r.conversions_linked),
      "Conversions Activated": n(r.conversions_activated),
      "New Members": n(r.new_members),
      "New-Member Value": n(r.new_member_value).toFixed(2),
    }));

  return (
    <div className="space-y-4">
      <ReportPageHeader
        title="MSR Book of Business"
        description={
          <>
            Conversions credited to each MSR — friends linked vs activated, new
            members, and new-member value. Credited to the booth rep who
            recorded the conversion (fallback: the event's sales hierarchy).
            "Unattributed" collects conversions with no resolvable rep.
          </>
        }
        actions={
          <ExportButton
            filters={filters}
            fetchPage={(f, p, l) =>
              getMsrBookOfBusiness(filterToRpc(f, p, l)).then((res) => ({
                rows: res.rows,
                total: res.total,
              }))
            }
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="msr-book-of-business"
          />
        }
      />

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showState={false}
        showType={false}
        showStatus={false}
        showOrg={false}
      />

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Sales Position</th>
              <th className="px-3 py-2 text-right">Linked</th>
              <th className="px-3 py-2 text-right">Activated</th>
              <th className="px-3 py-2 text-right">New Members</th>
              <th className="px-3 py-2 text-right">New-Member $</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  No conversions credited yet.
                </td>
              </tr>
            )}
            {rows.map((r: MsrBookRow) => (
              <tr
                key={r.position_id ?? "unattributed"}
                className="hover:bg-gray-50"
              >
                <td className="px-3 py-2">
                  {r.position_name || "Unattributed"}
                  {r.position_title && r.position_name !== r.position_title ? (
                    <span className="ml-1 text-xs text-gray-400">
                      ({r.position_title})
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right">
                  {n(r.conversions_linked)}
                </td>
                <td className="px-3 py-2 text-right">
                  {n(r.conversions_activated)}
                </td>
                <td className="px-3 py-2 text-right">{n(r.new_members)}</td>
                <td className="px-3 py-2 text-right">
                  {money(r.new_member_value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} total`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default MsrBookOfBusiness;
